import React, { useState } from 'react';
import { Reward, RewardType, RewardTypeLabels } from '../../types/reward';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';

interface RewardEditorProps {
  reward: Reward;
  onChange: (reward: Reward) => void;
  level?: number;
}

const AvailableRewardTypes = [
  RewardType.Credits,
  RewardType.Postcard,
  RewardType.GiftBox,
  RewardType.RandomBooster,
  RewardType.TargetBooster,
  RewardType.ChallengeBooster,
];

export const RewardEditor: React.FC<RewardEditorProps> = ({
  reward,
  onChange,
  level,
}) => {
  const [localReward, setLocalReward] = useState<Reward>(reward);

  React.useEffect(() => {
    setLocalReward(reward);
  }, [reward]);

  const updateReward = (updates: Partial<Reward>) => {
    const updated = { ...localReward, ...updates };
    setLocalReward(updated);
    onChange(updated);
  };

  const handleTypeChange = (newType: RewardType) => {
    const updated: Reward = {
      type: newType,
      amount: newType === RewardType.Postcard || newType === RewardType.GiftBox ? 1 : localReward.amount,
      innerRewards: newType === RewardType.GiftBox ? (localReward.innerRewards || []) : [],
    };
    setLocalReward(updated);
    onChange(updated);
  };

  const handleAmountChange = (e: InputNumberValueChangeEvent) => {
    const newAmount = e.value || 0;
    if (localReward.type === RewardType.Postcard && newAmount !== 1) {
      return;
    }
    if (localReward.type === RewardType.GiftBox && newAmount !== 1) {
      return;
    }
    updateReward({ amount: newAmount });
  };

  const addInnerReward = () => {
    const newInnerReward: Reward = {
      type: RewardType.Credits,
      amount: 0,
      innerRewards: [],
    };
    const updated = {
      ...localReward,
      innerRewards: [...(localReward.innerRewards || []), newInnerReward],
    };
    setLocalReward(updated);
    onChange(updated);
  };

  const updateInnerReward = (index: number, innerReward: Reward) => {
    const updated = {
      ...localReward,
      innerRewards: localReward.innerRewards?.map((r, i) =>
        i === index ? innerReward : r
      ) || [],
    };
    setLocalReward(updated);
    onChange(updated);
  };

  const removeInnerReward = (index: number) => {
    const updated = {
      ...localReward,
      innerRewards: localReward.innerRewards?.filter((_, i) => i !== index) || [],
    };
    setLocalReward(updated);
    onChange(updated);
  };

  const rewardTypeOptions = AvailableRewardTypes.map((type) => ({
    label: RewardTypeLabels[type],
    value: type,
  }));

  return (
    <div className="reward-editor">
      <div className="field mb-3">
        <label htmlFor="rewardType" className="block mb-2">
          Reward Type *
        </label>
        <Dropdown
          id="rewardType"
          value={localReward.type}
          options={rewardTypeOptions}
          onChange={(e) => handleTypeChange(e.value)}
          className="w-full"
        />
      </div>

      <div className="field mb-3">
        <label htmlFor="rewardAmount" className="block mb-2">
          Reward Amount *
        </label>
        <InputNumber
          id="rewardAmount"
          value={localReward.amount}
          onValueChange={handleAmountChange}
          disabled={localReward.type === RewardType.Postcard || localReward.type === RewardType.GiftBox}
          className="w-full"
        />
        {localReward.type === RewardType.Postcard && (
          <small className="text-color-secondary">
            Postcard amount is always 1
          </small>
        )}
        {localReward.type === RewardType.GiftBox && (
          <small className="text-color-secondary">
            GiftBox amount is always 1
          </small>
        )}
      </div>

      {localReward.type === RewardType.GiftBox && (level === undefined || level < 1) && (
        <div className="field mb-3">
          <div className="flex align-items-center justify-content-between mb-2">
            <label className="block">Inner Rewards</label>
            <Button
              icon="pi pi-plus"
              label="Add"
              size="small"
              onClick={addInnerReward}
            />
          </div>
          {localReward.innerRewards && localReward.innerRewards.length > 0 && (
            <div className="inner-rewards-list">
              {localReward.innerRewards.map((innerReward, index) => (
                <Card key={index} className="mb-2">
                  <div className="flex align-items-center justify-content-between mb-2">
                    <span className="font-semibold">Inner Reward {index + 1}</span>
                    <Button
                      icon="pi pi-trash"
                      onClick={() => removeInnerReward(index)}
                      rounded
                      text
                      size="small"
                      severity="danger"
                    />
                  </div>
                  <RewardEditor
                    reward={innerReward}
                    onChange={(updated) => updateInnerReward(index, updated)}
                    level={(level || 0) + 1}
                  />
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
